import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AddOutlined, DeleteOutline, SearchOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { inventoryService, productsService } from "../../services";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import { addTab } from "../../tabs/tabsSlice";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const nowDateString = () => new Date().toISOString().slice(0, 10);

const sanitizeIntegerInput = (value) => {
  const next = String(value ?? "").replace(/[^\d]/g, "");
  if (!next) return "";
  return String(Math.max(1, parseInt(next, 10)));
};

const buildPresentationOptions = (productDetail) => {
  const product = productDetail?.product || productDetail;
  const rows = toArray(productDetail?.presentations)
    .filter((row) => row?.is_active)
    .sort(
      (left, right) =>
        Number(left?.qty_in_base || 0) - Number(right?.qty_in_base || 0) ||
        Number(left?.id || 0) - Number(right?.id || 0)
    );

  return rows.map((presentation) => ({
    id: presentation.id,
    product_id: product?.id || null,
    product_description: product?.description || "Producto",
    product_code: product?.code || "",
    presentation_name: presentation?.product_format?.name || "Presentacion",
    qty_in_base: Number(presentation?.qty_in_base || 0),
  }));
};

const selectDefaultPresentationOption = (options = []) => {
  if (!Array.isArray(options) || options.length === 0) return null;
  return options[0];
};

function CreateInboundNotePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;

  const canCreate = Boolean(
    isSuperuser || effectivePerms.includes("inventory_inbound_create")
  );
  const canViewEntries = Boolean(
    isSuperuser || effectivePerms.includes("inventory_inbound_view")
  );

  const [reference, setReference] = useState("");
  const [comment, setComment] = useState("");
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [presentationOptionsByProductId, setPresentationOptionsByProductId] = useState({});

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  useEffect(() => {
    dispatch(
      addTab({
        path: "/inventory/inbound/create",
        label: "Nueva nota de entrada",
        closable: true,
      })
    );
  }, [dispatch]);

  const mergePresentationOptions = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setPresentationOptionsByProductId((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    const query = searchTerm.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list({
          search: query,
          active: "true",
          sales_search: "true",
          branch_id: branchId,
          limit: 20,
        });
        if (!cancelled) {
          const results = toArray(data);
          const nextOptionsByProductId = {};
          results.forEach((product) => {
            const options = buildPresentationOptions(product);
            if (options.length > 0) {
              nextOptionsByProductId[String(product.id)] = options;
            }
          });
          mergePresentationOptions(nextOptionsByProductId);
          setSearchResults(results);
        }
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [branchId, mergePresentationOptions, searchTerm]);

  const loadPresentationOptionsForProduct = useCallback(
    async (product) => {
      const productId = Number(product?.id);
      if (!Number.isInteger(productId) || productId <= 0) return [];

      const cached = presentationOptionsByProductId[String(productId)];
      if (Array.isArray(cached) && cached.length > 0) {
        return cached;
      }

      try {
        const detail = await productsService.get(productId);
        const options = buildPresentationOptions(detail);
        mergePresentationOptions({ [String(productId)]: options });
        return options;
      } catch {
        return [];
      }
    },
    [mergePresentationOptions, presentationOptionsByProductId]
  );

  const getItemOptions = useCallback(
    (item) => {
      const productId = String(item?.product_id || "");
      const mapped = presentationOptionsByProductId[productId];
      if (Array.isArray(mapped) && mapped.length > 0) return mapped;

      return item?.product_presentation_id
        ? [
            {
              id: item.product_presentation_id,
              product_id: item.product_id || null,
              product_description: item.product_description || "Producto",
              product_code: item.product_code || "",
              presentation_name: item.presentation_name || "Presentacion",
              qty_in_base: Number(item.qty_in_base || 0),
            },
          ]
        : [];
    },
    [presentationOptionsByProductId]
  );

  const handleAddProduct = useCallback(
    async (product) => {
      const options = await loadPresentationOptionsForProduct(product);
      const selectedOption = selectDefaultPresentationOption(options);

      if (!selectedOption) {
        showSnack("El producto no tiene presentaciones activas disponibles.", "warning");
        return;
      }

      setItems((prev) => {
        const existingIndex = prev.findIndex(
          (row) => Number(row.product_presentation_id) === Number(selectedOption.id)
        );
        if (existingIndex >= 0) {
          return prev.map((row, index) => {
            if (index !== existingIndex) return row;
            const currentQty = Number(row.qty_presentation || 1);
            return { ...row, qty_presentation: String(Math.max(1, currentQty + 1)) };
          });
        }

        return [
          ...prev,
          {
            id: `${selectedOption.id}-${Date.now()}`,
            product_id: selectedOption.product_id,
            product_code: selectedOption.product_code,
            product_presentation_id: selectedOption.id,
            product_description: selectedOption.product_description,
            presentation_name: selectedOption.presentation_name,
            qty_in_base: selectedOption.qty_in_base,
            qty_presentation: "1",
          },
        ];
      });

      setSearchTerm("");
      setSearchResults([]);
    },
    [loadPresentationOptionsForProduct]
  );

  const removeRow = (rowId) => {
    setItems((prev) => prev.filter((row) => row.id !== rowId));
  };

  const updateRowQty = (rowId, rawValue) => {
    setItems((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, qty_presentation: sanitizeIntegerInput(rawValue) } : row
      )
    );
  };

  const handlePresentationChange = (rowId, nextPresentationId) => {
    if (!nextPresentationId) return;

    setItems((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const options = getItemOptions(row);
        const nextOption = options.find((option) => Number(option.id) === Number(nextPresentationId));
        if (!nextOption) return row;

        return {
          ...row,
          product_presentation_id: nextOption.id,
          presentation_name: nextOption.presentation_name,
          qty_in_base: nextOption.qty_in_base,
        };
      })
    );
  };

  const normalizedItems = useMemo(
    () =>
      items
        .map((row) => ({
          product_presentation_id: Number(row.product_presentation_id),
          qty_presentation: Number(row.qty_presentation),
        }))
        .filter((row) => Number.isInteger(row.product_presentation_id) && row.product_presentation_id > 0)
        .filter((row) => Number.isInteger(row.qty_presentation) && row.qty_presentation > 0),
    [items]
  );

  const handleSave = async () => {
    if (submittingRef.current) return;

    if (!canCreate) {
      showSnack("No autorizado para crear notas de entrada.", "warning");
      return;
    }
    if (!branchId) {
      showSnack("No hay sucursal activa.", "warning");
      return;
    }
    if (normalizedItems.length === 0) {
      showSnack("Agrega al menos un item valido.", "warning");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const savedNote = await inventoryService.createInboundNote({
        branch_id: branchId,
        reference: reference.trim(),
        comment: comment.trim(),
        items: normalizedItems,
      });
      if (!savedNote?.id) {
        throw new Error("La nota de entrada no devolvio un id valido.");
      }
      showSnack("Nota de entrada creada correctamente.", "success");
      if (canViewEntries) {
        navigate("/inventory/inbound-notes");
      } else {
        setReference("");
        setComment("");
        setItems([]);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo crear la nota de entrada.";
      showSnack(detail, "error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para crear notas de entrada.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <MainText
          title={"Nueva nota de entrada"}
          subtitle={"Captura productos y ajusta su formato directamente en la fila"}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => (canViewEntries ? navigate("/inventory/inbound-notes") : navigate(-1))}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert sx={{ mb: 2 }} />

      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "350px 1fr", gap: 2 }}>
        <Card sx={{ borderLeft: `4px solid ${theme.palette.primary.main}` }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.25 }}>
              Datos generales
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Referencia"
                size="small"
                placeholder="Se genera automaticamente si lo dejas vacio"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                helperText="Si no ingresas una referencia, el sistema crea una automaticamente."
                fullWidth
              />
              <TextField
                label="Comentario"
                size="small"
                multiline
                minRows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                fullWidth
              />
              <TextField
                label="Sucursal"
                size="small"
                value={currentBranch?.name || "-"}
                fullWidth
                disabled
              />
              <TextField
                label="Fecha"
                size="small"
                type="date"
                value={nowDateString()}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: `4px solid ${theme.palette.secondary?.main || theme.palette.primary.light}`, minWidth: 0 }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
              Productos
            </Typography>

            <TextField
              label="Buscar producto"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Busca por nombre o codigo y agrega el producto. Luego ajustas el formato en la misma fila."
              fullWidth
            />

            {searchTerm.trim().length >= 2 && (
              <Paper variant="outlined" sx={{ maxHeight: 210, overflowY: "auto", p: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Buscando...
                  </Typography>
                ) : searchResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin resultados.
                  </Typography>
                ) : (
                  <Stack spacing={0.7}>
                    {searchResults.map((row) => {
                      const options = presentationOptionsByProductId[String(row.id)] || [];
                      return (
                        <Stack
                          key={row.id}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ p: 0.9, borderRadius: 1, bgcolor: "background.default" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                              {row.description || "Producto"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {row.code || "-"} · {options.length} formato{options.length === 1 ? "" : "s"} disponible
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddOutlined fontSize="small" />}
                            onClick={() => handleAddProduct(row)}
                          >
                            Agregar
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            )}

            <Paper variant="outlined" sx={{ minHeight: 260, maxHeight: "calc(100vh - 410px)", overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Formato</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Eliminar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Busca productos y agrégalos. El formato lo puedes cambiar directamente en la fila.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const options = getItemOptions(row);
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.product_description || "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.product_code || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={row.product_presentation_id || ""}
                              onChange={(event) => handlePresentationChange(row.id, Number(event.target.value))}
                              sx={{ minWidth: 180 }}
                            >
                              {options.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.presentation_name} ({Number(option.qty_in_base || 0).toFixed(3)})
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={row.qty_presentation}
                              onChange={(event) => updateRowQty(row.id, event.target.value)}
                              inputProps={{ min: 1, step: "1" }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => removeRow(row.id)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="contained" onClick={handleSave} disabled={submitting}>
                {submitting ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default CreateInboundNotePage;
